import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useMemo, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import LocalizedLink from "@/components/LocalizedLink";
import { ArrowRight } from "lucide-react";

// Hero images live in /public/hero/ (static, not Vite-hashed) so the homepage
// can preload the selected LCP image without preloading it on unrelated routes.
const heroBgADesktop = "/hero/hero-bg-desktop.webp";
const heroBgAMobile = "/hero/hero-bg-mobile.webp";
const heroBgBDesktop = "/hero/hero-bg-b-desktop.webp";
const heroBgBMobile = "/hero/hero-bg-b-mobile.webp";

// ── A/B test — hero background image ──────────────────────────────────────────
// Variant A: original hero (1920×1088 JPEG) — control
// Variant B: privileges banner (1500×837 WebP, 146KB) — challenger
// Assignment: 50/50 random, persisted in localStorage (te_hero_ab)
// Tracked: pushed to dataLayer as "hero_ab_view" → GA4 → compare CTA click rate

const AB_KEY = "te_hero_ab";

function getHeroVariant(): "a" | "b" {
  try {
    const stored = localStorage.getItem(AB_KEY);
    if (stored === "a" || stored === "b") return stored;
    const v: "a" | "b" = Math.random() < 0.5 ? "a" : "b";
    localStorage.setItem(AB_KEY, v);
    return v;
  } catch {
    return "a"; // fallback if localStorage unavailable
  }
}

const HeroSection = () => {
  const { t } = useTranslation();
  const variant = useMemo(() => getHeroVariant(), []);
  const heroBgDesktop = variant === "b" ? heroBgBDesktop : heroBgADesktop;
  const heroBgMobile = variant === "b" ? heroBgBMobile : heroBgAMobile;

  // Push variant assignment to dataLayer once — GTM/GA4 will track it
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "hero_ab_view",
      hero_variant: variant,                                        // "a" | "b"
      hero_image: variant === "b" ? "privileges-banner" : "original",
    });
  }, [variant]);

  return (
    <>
      <Helmet>
        <link
          rel="preload"
          as="image"
          href={heroBgDesktop}
          imageSrcSet={`${heroBgMobile} 768w, ${heroBgDesktop} 1920w`}
          imageSizes="100vw"
          fetchPriority="high"
        />
      </Helmet>

      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
        {/* Background — absolute fill avoids image-load CLS; width/height give the
            browser an aspect ratio for bandwidth hints even though display is object-cover. */}
        <div className="absolute inset-0">
          <picture>
            <source media="(max-width: 767px)" srcSet={heroBgMobile} type="image/webp" />
            <source media="(min-width: 768px)" srcSet={heroBgDesktop} type="image/webp" />
            <img
              src={heroBgDesktop}
              alt="Thailand Elite — Long stay visa memberships with luxury privileges in Thailand"
              className="w-full h-full object-cover"
              width={1920}
              height={1088}
              fetchPriority="high"
              decoding="async"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-hero" />
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto"
          >
            <h1 className="font-display font-bold text-primary-foreground leading-tight mb-4">
              <span className="block text-4xl sm:text-5xl lg:text-6xl">
                {t("hero.title")}
              </span>
              {/* JSX drops whitespace between elements on separate lines, so the
                  two spans concatenate in the plain-text extraction crawlers and
                  answer engines read: "…Visa5 to 20 Year…". The explicit space
                  is invisible on screen (both spans are block) but keeps the H1
                  readable as text. */}
              {" "}
              <span className="block mt-2 text-2xl sm:text-3xl lg:text-4xl font-semibold text-primary-foreground/90">
                {t("hero.titleSub")}
              </span>
            </h1>
            <p className="text-xl sm:text-2xl lg:text-3xl font-display italic text-pagoda-gold mb-6">
              {t("hero.subtitle")}
            </p>
            <p className="text-base sm:text-lg text-primary-foreground/80 font-body mb-10 max-w-xl mx-auto">
              {t("hero.description")}
            </p>
            <LocalizedLink
              to="/apply"
              className="inline-flex items-center gap-2 px-8 py-4 bg-accent text-accent-foreground font-body font-semibold text-base rounded-md hover:opacity-90 transition-all shadow-gold group"
            >
              {t("hero.cta")}
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </LocalizedLink>
          </motion.div>
        </div>

        {/* Bottom navigation tabs */}
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 border-t border-primary-foreground/20">
              {[
                { key: "overview",    anchor: "#overview"    },
                { key: "advantages",  anchor: "#advantages"  },
                { key: "eligibility", anchor: "#eligibility" },
                { key: "membership",  anchor: "#membership"  },
              ].map((tab) => (
                <a
                  key={tab.key}
                  href={tab.anchor}
                  className="py-4 text-center text-xs sm:text-sm font-body font-semibold tracking-widest text-primary-foreground/70 hover:text-pagoda-gold transition-colors border-r border-primary-foreground/10 last:border-r-0"
                >
                  {t(`hero.tabs.${tab.key}`)}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
